import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, addDoc, getDocs, doc, updateDoc, deleteDoc } from 'firebase/firestore';
import { FaEdit, FaTrashAlt } from 'react-icons/fa';

const categories = ['Food', 'Electronics', 'Clothing', 'Home', 'Beauty', 'Books'];

const AdminProducts = () => {
  const [products, setProducts] = useState([]);
  const [stores, setStores] = useState([]);
  const [selectedStoreFilter, setSelectedStoreFilter] = useState('all'); // For filtering
  const [showModal, setShowModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [form, setForm] = useState({
    name: '',
    price: '',
    category: categories[0],
    storeId: '', 
    images: ['', '', ''],
    description: '',
  });

  // Fetch products
  const fetchProducts = async () => {
    try {
      const querySnapshot = await getDocs(collection(db, 'products'));
      const items = querySnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
      setProducts(items);
    } catch (err) {
      console.error('Fetch products error:', err);
    }
  };

  // Fetch stores
  const fetchStores = async () => {
    try {
      const snapshot = await getDocs(collection(db, 'Stores'));
      const storesData = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
      setStores(storesData);
      if (storesData.length > 0) {
        setForm((prev) => ({ ...prev, storeId: storesData[0].id }));
      }
    } catch (err) {
      console.error('Fetch stores error:', err);
    }
  };

  useEffect(() => {
    fetchProducts();
    fetchStores();
  }, []);

  const openAddModal = () => {
    setEditingProduct(null);
    setForm({
      name: '',
      price: '',
      category: categories[0],
      storeId: stores.length > 0 ? stores[0].id : '',
      images: ['', '', ''],
      description: '',
    });
    setShowModal(true);
  };

  const openEditModal = (product) => {
    setEditingProduct(product);
    setForm({
      name: product.name,
      price: product.price,
      category: product.category,
      storeId: product.storeId || (stores.length > 0 ? stores[0].id : ''),
      images: product.images || ['', '', ''],
      description: product.description,
    });
    setShowModal(true);
  };

  const closeModal = () => setShowModal(false);

  const handleChange = (e, index = null) => {
    const { name, value } = e.target;
    if (name === 'images' && index !== null) {
      const updatedImages = [...form.images];
      updatedImages[index] = value;
      setForm((prev) => ({ ...prev, images: updatedImages }));
    } else {
      setForm((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.price || !form.images[0] || !form.description || !form.storeId) {
      alert('Please fill all required fields.');
      return;
    }

    try {
      const productData = {
        name: form.name,
        price: parseFloat(form.price),
        category: form.category,
        storeId: form.storeId,
        images: form.images.filter((img) => img.trim() !== ''),
        description: form.description,
      };

      if (editingProduct) {
        await updateDoc(doc(db, 'products', editingProduct.id), productData);
        alert('Product updated!');
      } else {
        await addDoc(collection(db, 'products'), productData);
        alert('Product added!');
      }

      fetchProducts();
      closeModal();
    } catch (err) {
      console.error('Error saving product:', err);
      alert('Failed to save product.');
    }
  };

  const handleDelete = async (product) => {
    if (!window.confirm(`Are you sure you want to delete "${product.name}"?`)) return;

    try {
      await deleteDoc(doc(db, 'products', product.id));
      alert('Product deleted!');
      fetchProducts();
    } catch (err) {
      console.error('Delete product error:', err);
      alert('Failed to delete product.');
    }
  };

  // Filter products by selected store
  const filteredProducts =
    selectedStoreFilter === 'all'
      ? products
      : products.filter((p) => p.storeId === selectedStoreFilter);

  return (
    <div className="p-6">
      <div className="flex flex-col md:flex-row justify-between items-center mb-4 gap-4">
        <h2 className="text-2xl font-bold">Admin Products</h2>
        <div className="flex gap-4">
          <select
            value={selectedStoreFilter}
            onChange={(e) => setSelectedStoreFilter(e.target.value)}
            className="border px-3 py-2 rounded"
          >
            <option value="all">All Stores</option>
            {stores.map((store) => (
              <option key={store.id} value={store.id}>
                {store.name}
              </option>
            ))}
          </select>
          <button onClick={openAddModal} className="bg-blue-600 text-white px-4 py-2 rounded">
            Add Product
          </button>
        </div>
      </div>

      <table className="w-full border-collapse border border-gray-300">
        <thead>
          <tr className="bg-gray-100">
            <th className="border border-gray-300 px-3 py-1">Images</th>
            <th className="border border-gray-300 px-3 py-1">Name</th>
            <th className="border border-gray-300 px-3 py-1">Price</th>
            <th className="border border-gray-300 px-3 py-1">Category</th>
            <th className="border border-gray-300 px-3 py-1">Store</th>
            <th className="border border-gray-300 px-3 py-1">Description</th>
            <th className="border border-gray-300 px-3 py-1">Actions</th>
          </tr>
        </thead>
        <tbody>
          {filteredProducts.map(({ id, name, price, category, storeId, description, images }) => (
            <tr key={id}>
              <td className="border border-gray-300 px-3 py-1 flex gap-2">
                {images?.map((img, idx) => (
                  <img key={idx} src={img} alt={`${name}-${idx}`} className="w-16 h-16 object-cover rounded" />
                ))}
              </td>
              <td className="border border-gray-300 px-3 py-1">{name}</td>
              <td className="border border-gray-300 px-3 py-1">Rs. {price.toFixed(2)}</td>
              <td className="border border-gray-300 px-3 py-1">{category}</td>
              <td className="border border-gray-300 px-3 py-1">
                {stores.find((s) => s.id === storeId)?.name || 'N/A'}
              </td>
              <td className="border border-gray-300 px-3 py-1">{description}</td>
              <td className="border border-gray-300 px-3 py-1 flex gap-2">
                <button
                  onClick={() => openEditModal({ id, name, price, category, storeId, description, images })}
                  className="text-indigo-600 hover:text-indigo-800"
                  title="Edit"
                >
                  <FaEdit />
                </button>
                <button
                  onClick={() => handleDelete({ id, name })}
                  className="text-red-600 hover:text-red-800"
                  title="Delete"
                >
                  <FaTrashAlt />
                </button>
              </td>
            </tr>
          ))}
          {filteredProducts.length === 0 && (
            <tr>
              <td colSpan="7" className="text-center py-4 text-gray-500 italic">
                No products found for selected store.
              </td>
            </tr>
          )}
        </tbody>
      </table>

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex justify-center items-center z-50">
          <form onSubmit={handleSubmit} className="bg-white p-6 rounded shadow max-w-md w-full space-y-4">
            <h3 className="text-xl font-semibold mb-2">{editingProduct ? 'Edit Product' : 'Add New Product'}</h3>

            <input
              name="name"
              placeholder="Name"
              value={form.name}
              onChange={handleChange}
              required
              className="w-full border px-3 py-2 rounded"
            />
            <input
              name="price"
              type="number"
              min="0"
              step="0.01"
              placeholder="Price"
              value={form.price}
              onChange={handleChange}
              required
              className="w-full border px-3 py-2 rounded"
            />

            <select
              name="category"
              value={form.category}
              onChange={handleChange}
              className="w-full border px-3 py-2 rounded"
            >
              {categories.map((cat) => (
                <option key={cat} value={cat}>
                  {cat}
                </option>
              ))}
            </select>

            {/* Store Dropdown in modal */}
            <select
              name="storeId"
              value={form.storeId}
              onChange={handleChange}
              className="w-full border px-3 py-2 rounded"
            >
              {stores.map((store) => (
                <option key={store.id} value={store.id}>
                  {store.name}
                </option>
              ))}
            </select>

            {form.images.map((img, idx) => (
              <input
                key={idx}
                name="images"
                placeholder={`Image ${idx + 1} URL${idx === 0 ? ' (required)' : ' (optional)'}`}
                value={img}
                onChange={(e) => handleChange(e, idx)}
                required={idx === 0}
                className="w-full border px-3 py-2 rounded"
              />
            ))}

            <textarea
              name="description"
              placeholder="Description"
              value={form.description}
              onChange={handleChange}
              required
              className="w-full border px-3 py-2 rounded"
            />

            <div className="flex justify-end space-x-4">
              <button type="button" onClick={closeModal} className="px-4 py-2 border rounded">
                Cancel
              </button>
              <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded">
                {editingProduct ? 'Update' : 'Add'}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default AdminProducts;
